import { useDeferredValue, useEffect, useState } from "react";
import { Bell, Heart, Search, ShoppingCart, SlidersHorizontal, UserCircle2 } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/shared/ui";
import { getGeoSearchResults } from "@/features/search/api/searchApi";

interface Props {
  isAuthenticated: boolean;
  dashboardHref: string;
  canonicalUrl?: string;
}

type QuickSearchDomain = "all" | "professionals" | "clinics" | "services" | "posts";

const QUICK_SEARCH_TABS: Array<{ key: QuickSearchDomain; label: string }> = [
  { key: "all", label: "Të gjitha" },
  { key: "professionals", label: "Mjekë" },
  { key: "clinics", label: "Klinika" },
  { key: "services", label: "Shërbimet" },
  { key: "posts", label: "Blogje" },
];

export const SmartHeader = ({ isAuthenticated, dashboardHref, canonicalUrl }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const canonical = canonicalUrl ?? (typeof window !== "undefined" ? `${window.location.origin}${pathname}` : "");

  useEffect(() => {
    if (!canonical) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;
    return () => link?.remove();
  }, [canonical]);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-card/95 px-4 py-3 shadow-sm backdrop-blur sm:px-6 lg:px-10">
      <div className="mx-auto flex max-w-7xl items-center gap-4">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="hidden h-8 w-8 place-items-center rounded-full bg-primary/10 sm:grid"><Heart className="h-4 w-4 text-primary" fill="currentColor" aria-hidden="true" /></span>
          <span className="brand-wordmark text-2xl leading-none text-sky-600 dark:text-sky-400 sm:text-xl">eshendetesia</span>
        </Link>

        <div className="mx-auto hidden max-w-xs flex-1 items-center gap-1.5 sm:flex lg:max-w-lg">
          {!isAuthenticated ? <PublicHeaderSearch /> : <Link to="/search" className="flex min-w-0 flex-1 items-center rounded-full border border-border bg-background px-4 py-2 text-sm text-muted-foreground hover:text-foreground"><Search className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />Kerko...</Link>}
          {!isAuthenticated ? <Link to="/search" title="Kerkim i avancuar" className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-xs font-semibold text-muted-foreground transition hover:border-primary/40 hover:text-primary"><SlidersHorizontal className="h-4 w-4" aria-hidden="true" /><span className="hidden xl:inline">Kerkim i avancuar</span></Link> : null}
        </div>

        <nav className="hidden items-center gap-3 text-sm font-medium text-muted-foreground lg:flex">
          <HeaderIconLink to="/app/favorites" icon={<Heart className="h-5 w-5" />} label="Preferencat" />
          <HeaderIconLink to="/app/cart" icon={<ShoppingCart className="h-5 w-5" />} label="Shporta" />
          <Link to="/about" className="ml-2 transition hover:text-primary">Rreth nesh</Link>
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          {!isAuthenticated ? <Link to="/search?advanced=1" title="Kerko" aria-label="Kerko" className="grid h-9 w-9 place-items-center rounded-full border border-border bg-background text-muted-foreground transition hover:border-primary/40 hover:text-primary sm:hidden"><Search className="h-4 w-4" aria-hidden="true" /></Link> : null}
          {isAuthenticated ? <><IconBtn icon={<Bell className="h-5 w-5" />} badge label="Njoftimet" /><IconBtn icon={<Heart className="h-5 w-5" />} badge label="Preferencat" /><IconBtn icon={<ShoppingCart className="h-5 w-5" />} badge label="Shporta" /><Link to={dashboardHref} aria-label="Profili im" className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary transition hover:bg-primary/20"><UserCircle2 className="h-5 w-5" aria-hidden="true" /></Link></> : <Button asChild className="h-9 rounded-full border border-primary/30 bg-transparent px-5 text-sm text-primary hover:bg-primary/10"><Link to="/login">Kycu</Link></Button>}
        </div>
      </div>
    </header>
  );
};

function HeaderIconLink({ to, icon, label }: { to: string; icon: JSX.Element; label: string }): JSX.Element {
  return <Link to={to} aria-label={label} title={label} className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-primary">{icon}</Link>;
}

function PublicHeaderSearch(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [activeDomain, setActiveDomain] = useState<QuickSearchDomain>("all");
  const deferredQuery = useDeferredValue(query.trim());
  const searchQuery = useQuery({
    queryKey: ["public-header-search", deferredQuery, activeDomain],
    queryFn: () => getGeoSearchResults({ q: deferredQuery, domain: activeDomain, page: 1, page_size: 4 }),
    enabled: deferredQuery.length >= 2,
    staleTime: 30_000,
  });
  const results = searchQuery.data?.results.slice(0, 4) ?? [];
  const isOpen = isFocused && deferredQuery.length >= 2;

  const submit = (): void => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (activeDomain !== "all") params.set("domain", activeDomain);
    navigate(params.size ? `/search?${params.toString()}` : "/search");
    setIsFocused(false);
  };

  return (
    <div className="relative min-w-0 flex-1">
      <form onSubmit={(event) => { event.preventDefault(); submit(); }} className="flex min-w-0 items-center rounded-full border border-border bg-background px-4 py-2 focus-within:border-primary/45 focus-within:ring-2 focus-within:ring-primary/10">
        <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => window.setTimeout(() => setIsFocused(false), 150)}
          placeholder="Kërko mjek, klinikë ose shërbim..."
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          aria-label="Kërkim i shpejtë"
        />
      </form>
      {isOpen ? (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 overflow-hidden rounded-xl border border-border bg-card p-1 shadow-xl">
          <div className="flex gap-1 overflow-x-auto border-b border-border px-1 pb-1.5 pt-0.5">
            {QUICK_SEARCH_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setActiveDomain(tab.key)}
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold transition ${activeDomain === tab.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {searchQuery.isLoading ? <p className="px-3 py-2 text-sm text-muted-foreground">Duke kërkuar...</p> : null}
          {!searchQuery.isLoading && results.length === 0 ? <p className="px-3 py-2 text-sm text-muted-foreground">Nuk u gjetën rezultate.</p> : null}
          {results.map((item) => (
            <Link key={`${item.domain}-${item.id}`} to={item.url || `/search?q=${encodeURIComponent(item.title)}`} className="flex items-center gap-3 rounded-lg px-3 py-2 transition hover:bg-muted" onClick={() => setIsFocused(false)}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Search className="h-3.5 w-3.5" aria-hidden="true" /></span>
              <span className="min-w-0"><span className="block truncate text-sm font-semibold text-foreground">{item.title}</span>{item.subtitle ? <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span> : null}</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function IconBtn({ icon, badge, label }: { icon: JSX.Element; badge?: boolean; label: string }): JSX.Element {
  return <button type="button" aria-label={label} className="relative grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:bg-muted">{icon}{badge ? <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-500" aria-hidden="true" /> : null}</button>;
}
